import { useKeyStore } from "../state/keyStore";
import { useStyleStore } from "../state/styleStore";
import { useToast } from "../state/toast";
import { useT } from "../i18n";
import type { StyledChart } from "../hooks/useStyledChart";
import type { StyleOption } from "../core/theory/styles";

// Play-style chips above the shape strip. Only the styles that fit this song are shown (none at all when
// just 원래대로 fits); one that needs another capo carries a capo tag and applies it, with an undo.
export function StyleChips({ chart }: { chart: StyledChart }) {
  const t = useT();
  const setStyle = useStyleStore((s) => s.set);
  if (chart.options.length < 2) return null;

  const pick = (o: StyleOption) => {
    if (o.capo != null) {
      const prev = { capo: useKeyStore.getState().capo, style: chart.style };
      useKeyStore.getState().set({ capo: o.capo });
      useToast.getState().show(t("style.capo_applied", { n: o.capo }), {
        label: t("style.undo"),
        run: () => { useKeyStore.getState().set({ capo: prev.capo }); setStyle(prev.style); },
      });
    }
    setStyle(o.style);
  };

  const desc = chart.style === "drone" ? t(chart.drone === "fret3" ? "style.desc_drone_fret3" : "style.desc_drone_open")
    : chart.style === "power" ? t("style.desc_power", { n: chart.barre })
    : chart.style === "orig" ? "" : t("style.desc_" + chart.style);

  return (
    <div className="style-bar">
      <div className="style-chips" role="radiogroup" aria-label={t("style.label")}>
        {chart.options.map((o) => {
          const on = o.style === chart.style && o.capo == null;
          return (
            <button key={o.style} role="radio" aria-checked={on} className={"style-chip" + (on ? " on" : "")} onClick={() => pick(o)}>
              {t("style." + o.style)}
              {o.capo != null && <span className="sc-tag">{t("key.capo_tag", { n: o.capo })}</span>}
              {o.recommended && <span className="sc-rec">{t("style.rec")}</span>}
            </button>
          );
        })}
      </div>
      {desc && <p className="style-desc" aria-live="polite">{desc} <span>{t("style.note")}</span></p>}
    </div>
  );
}
